import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BaseService } from '@aifs-shared/common/base-service';
import { ResponseData } from '@aifs-shared/common/response-data';
import { BaseResponse } from '@aifs-shared/common/base-response';

import { Observable, Subject } from 'rxjs';

import { VisaOverview } from './visa-overview';
import { VisaSummary } from './visa-summary';
import { VisaInfo } from './visa-information';
import { environment } from '@environments/environment';

@Injectable()
export class VisaService extends BaseService {

    constructor(private http: HttpClient) {
        super();
    }

    public getVisaOverview(applicationId: number): Observable<VisaOverview> {
        let subject = new Subject<VisaOverview>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_Visa_Status(applicationId))
            .subscribe({
                next: (data: BaseResponse) => {
                    let response = this.getResultData<VisaOverview>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    this.handleError(error);
                }
            });

        return subject;
    }

    public getVisaInformation(applicationId: number): Observable<VisaInfo> {
        let subject = new Subject<VisaInfo>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_Visa_Information(applicationId))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<VisaInformationResponse>(data);
                    if (response.details) subject.next(response.details);
                    else subject.error('No details returned from call');
                },
                error: (error: any) => {
                    this.handleError(error);
                }
            });

        return subject;
    }

    public updateVisaStatus(applicationId: number, orcaApplicationId: number, orcaMemberNumber: number, appointmentDateTime: string, embassyName: string, visaStatus: string, passportStatus: string): Observable<VisaStatusUpdate> {
        let subject = new Subject<VisaStatusUpdate>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Visa_Status_Update(applicationId),
                {
                    'id': applicationId,
                    'orcaApplicationId': orcaApplicationId,
                    'orcaMemberNumber': orcaMemberNumber,
                    'appointmentDateTime': appointmentDateTime,
                    'embassyName': embassyName,
                    'visaStatus': visaStatus,
                    'passportStatus': passportStatus
                }
            )
            .subscribe({
                next: (data: BaseResponse) => {
                    let response = this.getResultData<VisaStatusUpdate>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    this.handleError(error);
                }
            });

        return subject;
    }

    public updateDS2019BlockStatus(applicationId: number, block: boolean): Observable<DS2019BlockStatusUpdate> {
        const s = new Subject<DS2019BlockStatusUpdate>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_DS2019_Block_Status_Update(applicationId),
                {
                    'id': applicationId,
                    'block': block
                }
            )
            .subscribe({
                next: (data: BaseResponse) => {
                    let response = this.getResultData<VisaStatusUpdate>(data);
                    s.next(response);
                },
                error: (error: any) => {
                    this.handleError(error);
                    s.error(error);
                }
            });
        return s;
    }   

    /**
     * Determine which text to display based upon the returned country of application
     */
    public getAdditionalTextByCountry(countryCode: string): number {
        switch (countryCode) {
            case "CA":
                return 0;
            case "AU":
                return 1;
            case "UA":
            case "BG":
            case "BY":
            case "CZ":
            case "PL":
            case "RO":
            case "RU":
                return 2;
            case "NZ":
                return 3;
            default:
                return 4;
        }
    }
}

export class VisaOverviewResponse implements ResponseData {
    result?: VisaOverview;
}

export class VisaStatusUpdate implements ResponseData {
    result?: boolean;
}

export class VisaSummaryResponse implements ResponseData { data?: VisaSummary; }

export class DS2019BlockStatusUpdate implements ResponseData {
    result?: boolean;
}

export class VisaInformationResponse implements ResponseData { details?: VisaInfo; }

export enum DS2019ResultCode {
    NotPrinted,
    CountryBlockedNoOverride,
    CountryBlockedOverride,
    CountryAvailableNoOverride,
    CountryAvailableOverride,
    PrintedBlockedInCountry,
    PrintedBlockedByOverride,
    PrintedAvailable
}

export enum DS2019ActionAvailable {
    None,
    InsertOverride,
    DeleteOverride
}